import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "dd_token";

const client = axios.create({ baseURL: API });

// Attach the JWT to every admin API call
client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// On 401 (expired token / signed out elsewhere), wipe the stale token
// and bounce to /login so the user knows exactly what to do — otherwise
// their action (Create Customer, Auto-suggest code, etc.) silently fails
// with a "Not authenticated" toast and looks like a bug.
client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      try {
        localStorage.removeItem(TOKEN_KEY);
      } catch { /* ignore */ }
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        // Preserve where they were trying to go
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/login?next=${next}&expired=1`);
      }
    }
    return Promise.reject(err);
  },
);

export const adminApi = {
  // Fetch any admin doc URL (PDF/Excel) via the authenticated axios client and
  // open the resulting blob in a new tab. Fixes "Not authenticated" errors that
  // happen when a plain <a href> loses the Authorization header on new-tab
  // navigations. Pass action="download" to force a download instead of preview.
  openDoc: async (url, { action = "open", filename } = {}) => {
    // Strip API base so client.baseURL applies uniformly
    const path = url.startsWith(API) ? url.slice(API.length) : url;
    const res = await client.get(path, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(res.data);
    if (action === "download") {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename || path.split("/").pop() || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    }
    // Revoke after a delay so the tab has time to load
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return blobUrl;
  },

  // Jobs
  listJobs: (params = {}) => client.get("/admin/jobs", { params }).then((r) => r.data),
  getJob: (id) => client.get(`/admin/jobs/${id}`).then((r) => r.data),
  createJob: (data) => client.post("/admin/jobs", data).then((r) => r.data),
  updateJob: (id, data) => client.put(`/admin/jobs/${id}`, data).then((r) => r.data),
  deleteJob: (id) => client.delete(`/admin/jobs/${id}`).then((r) => r.data),

  // Bookings
  listBookings: (params = {}) => client.get("/admin/bookings", { params }).then((r) => r.data),
  createBooking: (data) => client.post("/admin/bookings", data).then((r) => r.data),
  updateBooking: (id, data) => client.put(`/admin/bookings/${id}`, data).then((r) => r.data),
  deleteBooking: (id) => client.delete(`/admin/bookings/${id}`).then((r) => r.data),
  checkBookingDuplicate: (registration_number) =>
    client.get("/admin/bookings/check-duplicate", { params: { registration_number } }).then((r) => r.data),

  // Dashboard
  dashboard: () => client.get("/admin/dashboard").then((r) => r.data),

  // Stock requests
  listStockRequests: (params = {}) => client.get("/admin/stock-requests", { params }).then((r) => r.data),
  confirmStockRequest: (id, confirmed_by) =>
    client.post(`/admin/stock-requests/${id}/confirm`, null, { params: { confirmed_by } }).then((r) => r.data),
  deleteStockRequest: (id) => client.delete(`/admin/stock-requests/${id}`).then((r) => r.data),

  // Notifications
  listNotifications: (params = {}) => client.get("/admin/notifications", { params }).then((r) => r.data),
  markNotificationRead: (id) => client.post(`/admin/notifications/${id}/read`).then((r) => r.data),
  markAllNotificationsRead: () => client.post("/admin/notifications/read-all").then((r) => r.data),

  // Quote inbox
  listQuotes: (params = {}) => client.get("/admin/quotes", { params }).then((r) => r.data),
  updateQuoteStatus: (id, status) => client.put(`/admin/quotes/${id}/status`, { status }).then((r) => r.data),
  convertQuoteToJob: (id) => client.post(`/admin/quotes/${id}/convert`).then((r) => r.data),
  // Quote-request client link (magic-link before conversion)
  createQuoteClientLink: (id, body = {}) =>
    client.post(`/admin/quotes/${id}/client-link`, body).then((r) => r.data),
  listQuoteClientLinks: (id) =>
    client.get(`/admin/quotes/${id}/client-links`).then((r) => r.data),

  // Aging
  aging: () => client.get("/admin/aging").then((r) => r.data),

  // Vehicle history
  vehicleHistory: (registration, exclude_job_id) =>
    client.get(`/admin/vehicles/${encodeURIComponent(registration)}/history`,
      { params: exclude_job_id ? { exclude_job_id } : {} }).then((r) => r.data),

  // Routes / inventory / fitter performance (Phase 2)
  routes: (date) => client.get("/admin/routes", { params: date ? { date } : {} }).then((r) => r.data),
  listInventory: (low_only = false) =>
    client.get("/admin/inventory", { params: low_only ? { low_only: true } : {} }).then((r) => r.data),
  createInventory: (data) => client.post("/admin/inventory", data).then((r) => r.data),
  updateInventory: (id, data) => client.put(`/admin/inventory/${id}`, data).then((r) => r.data),
  adjustInventory: (id, delta) =>
    client.post(`/admin/inventory/${id}/adjust`, null, { params: { delta } }).then((r) => r.data),
  deleteInventory: (id) => client.delete(`/admin/inventory/${id}`).then((r) => r.data),
  fitterPerformance: () => client.get("/admin/fitters").then((r) => r.data),

  // Customers (Phase 3 — Pastel-style register + statements)
  listCustomers: (params = {}) => client.get("/admin/customers", { params }).then((r) => r.data),
  getCustomer: (id) => client.get(`/admin/customers/${id}`).then((r) => r.data),
  createCustomer: (data) => client.post("/admin/customers", data).then((r) => r.data),
  updateCustomer: (id, data) => client.put(`/admin/customers/${id}`, data).then((r) => r.data),
  deleteCustomer: (id) => client.delete(`/admin/customers/${id}`).then((r) => r.data),
  archiveCustomer: (id) => client.post(`/admin/customers/${id}/archive`).then((r) => r.data),
  restoreCustomer: (id) => client.post(`/admin/customers/${id}/restore`).then((r) => r.data),
  suggestCustomerCode: (name) =>
    client.get("/admin/customers/suggest-code", { params: { name } }).then((r) => r.data),
  customerHistory: (id) => client.get(`/admin/customers/${id}/history`).then((r) => r.data),
  customerStatement: (id, date_from, date_to) =>
    client.get(`/admin/customers/${id}/statement`, {
      params: { ...(date_from ? { date_from } : {}), ...(date_to ? { date_to } : {}) },
    }).then((r) => r.data),

  // Jobs: void / unvoid
  voidJob: (id, reason) => client.post(`/admin/jobs/${id}/void`, null, { params: reason ? { reason } : {} }).then((r) => r.data),
  unvoidJob: (id) => client.post(`/admin/jobs/${id}/unvoid`).then((r) => r.data),
  issueTaxInvoice: (id, actor) => client.post(`/admin/jobs/${id}/issue-tax-invoice`, null, { params: actor ? { actor } : {} }).then((r) => r.data),

  // Bundle & Send — email Invoice + Claim Form + Job Card PDFs to insurer
  bundleSend: (id, body) => client.post(`/admin/jobs/${id}/bundle-send`, body).then((r) => r.data),
  // Notify client of current job status via email + magic-link
  notifyStatus: (id, body = {}) => client.post(`/admin/jobs/${id}/notify-status`, body).then((r) => r.data),

  // Brokers (optional intermediary directory)
  listBrokers: (params = {}) => client.get("/admin/brokers", { params }).then((r) => r.data),
  getBroker: (id) => client.get(`/admin/brokers/${id}`).then((r) => r.data),
  createBroker: (data) => client.post("/admin/brokers", data).then((r) => r.data),
  updateBroker: (id, data) => client.put(`/admin/brokers/${id}`, data).then((r) => r.data),
  deleteBroker: (id) => client.delete(`/admin/brokers/${id}`).then((r) => r.data),

  // End-user Clients + Vehicles (search by reg/VIN)
  listClients: (params = {}) => client.get("/admin/clients", { params }).then((r) => r.data),
  getClient: (id) => client.get(`/admin/clients/${id}`).then((r) => r.data),
  createClient: (data) => client.post("/admin/clients", data).then((r) => r.data),
  updateClient: (id, data) => client.put(`/admin/clients/${id}`, data).then((r) => r.data),
  deleteClient: (id) => client.delete(`/admin/clients/${id}`).then((r) => r.data),
  lookupClientByVehicle: (params) => client.get("/admin/clients/lookup", { params }).then((r) => r.data),
  addClientVehicle: (clientId, data) => client.post(`/admin/clients/${clientId}/vehicles`, data).then((r) => r.data),
  updateClientVehicle: (clientId, vehicleId, data) =>
    client.put(`/admin/clients/${clientId}/vehicles/${vehicleId}`, data).then((r) => r.data),
  removeClientVehicle: (clientId, vehicleId) =>
    client.delete(`/admin/clients/${clientId}/vehicles/${vehicleId}`).then((r) => r.data),

  // Vehicle history — all jobs ever for a given reg number (across ownership)
  vehicleHistory: (registration, exclude_job_id) =>
    client.get(`/admin/vehicles/${encodeURIComponent(registration)}/history`,
               { params: exclude_job_id ? { exclude_job_id } : {} }).then((r) => r.data),

  // Bulk consolidate — un-invoiced completed jobs for a customer
  consolidatableJobs: (customerId, params = {}) =>
    client.get(`/admin/customers/${customerId}/consolidatable-jobs`, { params }).then((r) => r.data),
  consolidateInvoice: (customerId, body) =>
    client.post(`/admin/customers/${customerId}/consolidate`, body).then((r) => r.data),

  // Standalone Tax Invoices (bulk / monthly)
  listInvoices: (params = {}) => client.get("/admin/invoices", { params }).then((r) => r.data),
  getInvoice: (id) => client.get(`/admin/invoices/${id}`).then((r) => r.data),
  createInvoice: (data) => client.post("/admin/invoices", data).then((r) => r.data),
  updateInvoice: (id, data) => client.put(`/admin/invoices/${id}`, data).then((r) => r.data),
  issueInvoice: (id) => client.post(`/admin/invoices/${id}/issue`).then((r) => r.data),
  voidInvoice: (id, reason) => client.post(`/admin/invoices/${id}/void`, { reason }).then((r) => r.data),
  unvoidInvoice: (id) => client.post(`/admin/invoices/${id}/unvoid`).then((r) => r.data),
  deleteInvoice: (id) => client.delete(`/admin/invoices/${id}`).then((r) => r.data),
  invoicePdfUrl: (id) => `${API}/admin/invoices/${id}/pdf.pdf`,
  addInvoicePayment: (id, body) => client.post(`/admin/invoices/${id}/payments`, body).then((r) => r.data),
  removeInvoicePayment: (id, paymentId) => client.delete(`/admin/invoices/${id}/payments/${paymentId}`).then((r) => r.data),

  // Credit notes
  listCreditNotes: (params = {}) => client.get("/admin/credit-notes", { params }).then((r) => r.data),
  createCreditNote: (data) => client.post("/admin/credit-notes", data).then((r) => r.data),
  getCreditNote: (id) => client.get(`/admin/credit-notes/${id}`).then((r) => r.data),
  deleteCreditNote: (id) => client.delete(`/admin/credit-notes/${id}`).then((r) => r.data),

  // Reports
  reportJobsByCustomer: (params = {}) => client.get("/admin/reports/jobs-by-customer", { params }).then((r) => r.data),
  reportRevenue: (params = {}) => client.get("/admin/reports/revenue", { params }).then((r) => r.data),
  reportOutstanding: () => client.get("/admin/reports/outstanding-by-customer").then((r) => r.data),
  reportInsurer: (params = {}) => client.get("/admin/reports/insurer", { params }).then((r) => r.data),

  // Excel report URLs (used directly as anchor href)
  reportExcelUrl: (path, params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return `${API}/admin/reports/${path}.xlsx${qs ? `?${qs}` : ""}`;
  },

  // PDF report URLs (used directly as anchor href — server sets Content-Disposition)
  reportPdfUrl: (path, params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return `${API}/admin/reports/${path}.pdf${qs ? `?${qs}` : ""}`;
  },

  // Per-document PDFs
  jobQuotePdfUrl: (job_id) => `${API}/admin/jobs/${job_id}/quote.pdf`,
  jobInvoicePdfUrl: (job_id) => `${API}/admin/jobs/${job_id}/invoice.pdf`,
  jobClaimFormPdfUrl: (job_id) => `${API}/admin/jobs/${job_id}/claim-form.pdf`,
  jobExcessInvoicePdfUrl: (job_id) => `${API}/admin/jobs/${job_id}/excess-invoice.pdf`,
  creditNotePdfUrl: (cn_id) => `${API}/admin/credit-notes/${cn_id}/pdf.pdf`,
  statementPdfUrl: (id, date_from, date_to) => {
    const qs = new URLSearchParams(
      Object.entries({ date_from, date_to }).filter(([, v]) => v)
    ).toString();
    return `${API}/admin/customers/${id}/statement.pdf${qs ? `?${qs}` : ""}`;
  },
  statementXlsxUrl: (id, date_from, date_to) => {
    const qs = new URLSearchParams(
      Object.entries({ date_from, date_to }).filter(([, v]) => v)
    ).toString();
    return `${API}/admin/customers/${id}/statement.xlsx${qs ? `?${qs}` : ""}`;
  },
  sendStatementEmail: (id, body, date_from, date_to) => {
    const qs = new URLSearchParams(
      Object.entries({ date_from, date_to }).filter(([, v]) => v)
    ).toString();
    return client.post(`/admin/customers/${id}/statement/email${qs ? `?${qs}` : ""}`, body).then((r) => r.data);
  },

  // Insurers register (with per-insurer excess defaults)
  listInsurers: (params = {}) => client.get("/admin/insurers", { params }).then((r) => r.data),
  getInsurer: (id) => client.get(`/admin/insurers/${id}`).then((r) => r.data),
  createInsurer: (body) => client.post("/admin/insurers", body).then((r) => r.data),
  updateInsurer: (id, body) => client.put(`/admin/insurers/${id}`, body).then((r) => r.data),
  deleteInsurer: (id) => client.delete(`/admin/insurers/${id}`).then((r) => r.data),

  // Job signature + excess payment + payment reminders
  saveJobSignature: (jobId, data_url, signed_by) =>
    client.post(`/admin/jobs/${jobId}/signature`, { data_url, signed_by }).then((r) => r.data),
  clearJobSignature: (jobId) =>
    client.delete(`/admin/jobs/${jobId}/signature`).then((r) => r.data),
  markExcessPaid: (jobId, body = {}) =>
    client.post(`/admin/jobs/${jobId}/excess-payment`, body).then((r) => r.data),
  clearExcessPaid: (jobId) =>
    client.delete(`/admin/jobs/${jobId}/excess-payment`).then((r) => r.data),
  runReminders: (params = {}) =>
    client.post("/admin/reminders/run", null, { params }).then((r) => r.data),
  remindersLog: (limit = 50) =>
    client.get("/admin/reminders/log", { params: { limit } }).then((r) => r.data),

  // Global search — searches customers, clients, vehicles, jobs, invoices
  search: (q, limit = 20) =>
    client.get("/admin/search", { params: { q, limit } }).then((r) => r.data),

  // Monthly statement batch run
  statementBatchPreview: (params = {}) =>
    client.get("/admin/statements/run-batch/preview", { params }).then((r) => r.data),
  statementBatchRun: (body) =>
    client.post("/admin/statements/run-batch", body).then((r) => r.data),

  // Dashboard chase list — overdue invoices grouped by customer for follow-up
  chaseList: (params = {}) => client.get("/admin/dashboard/chase-list", { params }).then((r) => r.data),

  // Settings (company + email/Resend)
  getSettings: () => client.get("/admin/settings").then((r) => r.data),
  updateSettings: (patch) => client.put("/admin/settings", patch).then((r) => r.data),
  sendTestEmail: (recipient_email) =>
    client.post("/admin/settings/email/test", { recipient_email }).then((r) => r.data),

  // Client magic-links
  createClientLink: (job_id, body = {}) =>
    client.post(`/admin/jobs/${job_id}/client-links`, body).then((r) => r.data),
  listClientLinks: (job_id) =>
    client.get(`/admin/jobs/${job_id}/client-links`).then((r) => r.data),
  revokeClientLink: (token, reason) =>
    client.post(`/admin/client-links/${token}/revoke`, { reason }).then((r) => r.data),
  sendClientLinkEmail: (token, body = {}) =>
    client.post(`/admin/client-links/${token}/send-email`, body).then((r) => r.data),

  // Testimonials
  listTestimonials: (status) =>
    client.get("/admin/testimonials", { params: status ? { status } : {} }).then((r) => r.data),
  createTestimonial: (data) => client.post("/admin/testimonials", data).then((r) => r.data),
  approveTestimonial: (id, approved_by) =>
    client.post(`/admin/testimonials/${id}/approve`, null, { params: approved_by ? { approved_by } : {} }).then((r) => r.data),
  rejectTestimonial: (id) => client.post(`/admin/testimonials/${id}/reject`).then((r) => r.data),
  deleteTestimonial: (id) => client.delete(`/admin/testimonials/${id}`).then((r) => r.data),

  // POPIA
  customerDataExportUrl: (id) => `${API}/admin/customers/${id}/data-export`,
  eraseCustomer: (id, actor) =>
    client.post(`/admin/customers/${id}/erase`, null, { params: { confirm: true, ...(actor ? { actor } : {}) } }).then((r) => r.data),
  auditLog: (params = {}) => client.get("/admin/audit-log", { params }).then((r) => r.data),
  runBackup: () => client.post("/admin/backup/run").then((r) => r.data),
  listBackups: () => client.get("/admin/backups").then((r) => r.data),
};

export const teamApi = {
  listJobs: (params = {}) => client.get("/team/jobs", { params }).then((r) => r.data),
  getJob: (id) => client.get(`/team/jobs/${id}`).then((r) => r.data),
  submitJob: (id, payload) => client.post(`/team/jobs/${id}/submit`, payload).then((r) => r.data),
  createStockRequest: (data) => client.post("/team/stock-requests", data).then((r) => r.data),
  listStockRequests: (params = {}) => client.get("/team/stock-requests", { params }).then((r) => r.data),
  sharePrefitment: (id, payload) =>
    client.post(`/team/jobs/${id}/share-prefitment`, payload).then((r) => r.data),
};

export const publicApi = {
  getPrefitment: (token) => client.get(`/public/prefitment/${token}`).then((r) => r.data),
  signPrefitment: (token, payload) =>
    client.post(`/public/prefitment/${token}/sign`, payload).then((r) => r.data),
  submitTestimonial: (payload) => client.post("/public/testimonials", payload).then((r) => r.data),
  listPublicTestimonials: (limit = 12) =>
    client.get("/public/testimonials", { params: { limit } }).then((r) => r.data),

  // Client magic-link portal
  getPortal: (token) => client.get(`/public/portal/${token}`).then((r) => r.data),
  acceptQuote: (token) => client.post(`/public/portal/${token}/accept-quote`).then((r) => r.data),
  portalDocUrl: (token, kind) => `${API}/public/portal/${token}/document/${kind}.pdf`,
};
